import { v4 as uuidv4 } from 'uuid';
import { DefaultSuccess } from 'type/http';
import Request from './request';
import { LoginSuccess } from 'type/login';
import { CompanyDisplayConfig } from 'type/company';

const request = new Request('pivot');
const authRequest = new Request('auth');
const bffRequest = new Request('');

export type RegisterMobile = {
  refreshToken: string;
  state: string;
  userId: string;
  tokenType: string;
  accessToken: string;
  expiresIn: number;
};

/**
 * 手机验证码登录 - 此接口用户未注册的话，会帮用户自动注册
 * @param mobileCaptcha 验证码
 * @param mobile 手机号
 */
export const loginByMobile = (mobileCaptcha: string, mobile: string, sessionId: string) => {
  return request.post<LoginSuccess>(
    '/api/pauth/token/mobile',
    {
      mobileCaptcha,
      mobile
    },
    {
      headers: {
        'x-pivot-auth': 'client',
        'x-session-id': sessionId
      }
    }
  );
};

/**
 * 发送手机验证码
 * @param mobile 手机号
 * @param type 1为注册，2为修改手机号，3为重置密码，4为微信捆绑，5位验证码登录，6为绑定手机号
 */
export const getMobileCaptchas = (mobile: string, type: number, slidingCaptcha?: any) => {
  return request.post<DefaultSuccess>(
    '/api/client/mobileCaptchas/slide',
    {
      mobile,
      type,
      slidingCaptcha: {
        appCode: 'maxhub-integration-server',
        ...slidingCaptcha
      }
    },
    {
      headers: {
        'x-pivot-auth': 'client'
      }
    }
  );
};

/**
 * 获取SessionId
 */
export const getSessionId = () => {
  return request.post<{ sessionId: string }>(
    '/api/pauth/token/session',
    {
      scope: 'basic',
      endpointId: uuidv4(),
      endpointType: 'WEB_APP'
    },
    {
      headers: {
        'x-pivot-auth': 'client'
      }
    }
  );
};

/**
 * 获取csrfToken
 */
export const getLoginCsrfToken = () => {
  return authRequest.get<{ csrfToken: string }>('/api/getLoginCsrfToken', {
    headers: {
      endpointId: uuidv4(),
      endpointType: 'WEB_APP'
    }
  });
};

export const loginOut = () => {
  return request.post<DefaultSuccess>('/api/user/auth/logout');
};

/**
 * 设置默认企业信息
 * @param config 企业信息
 */
export const companyDisplayConfig = (config: CompanyDisplayConfig) => {
  return request.post<DefaultSuccess>('/api/user/companyDisplayConfig', {
    ...config
  });
};

type AuthRes = { codeId: string; code: string; createdDate: string; expiredDate: string };
export const fetchWeappAuth = () => {
  return request.get<AuthRes>(`/api/client/webappAuth/getCode?codeId=${uuidv4()}`);
};

/**
 * 查询员工信息（根据companyId和userId）
 */
export const getStaffInfo = (companyId: string, userId: string) => {
  return request.get(`/api/client/staff/getByCompanyIdAndUserId?companyId=${companyId}&userId=${userId}`);
};

/**
 * 查询用户信息（根据accessToken）
 */
export const getUserInfoByToken = (accessToken: string) => {
  return request.get('/api/client/auth/userinfo', { params: { access_token: accessToken } });
};

/**
 * 查询wx用户token
 */
export const getWeChatToken = () => {
  return authRequest.get('/api/weChatLogin');
};

/**
 * 查询wx登录配置
 */
export const getWxLoginConfig = () => {
  return authRequest.get('/api/getWxConfig');
};

/**
 * 清除多余的cookies
 */
export const clearCookies = () => {
  return authRequest.get('/api/clearCookies');
};

/**
 * 解绑wx账号 调试使用
 */
export const unbindWxAccount = (openid: string) => {
  return request.post('/api/user/account/weixin/actions/unbind', { openid });
};

// 设置用户员工身份，如果登录后未设置，则当前用户为个人身份，设置了员工身份后，即为对应企业的员工身份
export const setStaff = (staffId: string) => {
  return request.post('/api/user/userSetting/setStaff', { staffId });
};

export const getStaff = () => {
  return request.get('/api/user/userSetting/getStaff');
};

export const ssoLogout = () => {
  return bffRequest.get('/sso/logout');
};

// 私有化，查询用户是否注册
export const checkUserRegistered = (username: string) => {
  return request.get('/api/client/accounts/user', {
    params: {
      username
    }
  });
};

// 密码登录
export const loginByPassword = ({ username, password, sessionId, graphicCaptcha }: any) => {
  return request.post(
    '/api/pauth/token/password',
    {
      username,
      password,
      graphicCaptcha
    },
    {
      headers: {
        'x-session-id': sessionId,
        'x-pivot-auth': 'client'
      }
    }
  );
};

/**
 * 修改密码
 */
export const modifyPassword = function ({
  mobile,
  mobileCaptcha,
  password,
  graphicCaptcha,
  sessionId
}: {
  mobile: string;
  mobileCaptcha: string;
  password: string;
  graphicCaptcha?: string;
  sessionId: string;
}) {
  return request.patch(`/api/client/accounts/${mobile}/password`, {
    mobile,
    mobileCaptcha,
    graphicCaptcha,
    password,
    sessionId
  });
};

/**
 * 私有化修改密码
 */
export const modifyPasswordPriv = function ({
  password,
  oldPassword,
  sessionId,
  graphicCaptcha
}: {
  password: string;
  oldPassword: string;
  sessionId: string;
  graphicCaptcha?: string;
}) {
  return request.patch(
    '/api/user/account/password-for-graphic-captcha',
    {
      password,
      oldPassword,
      graphicCaptcha
    },
    {
      headers: {
        'x-session-id': sessionId
      }
    }
  );
};

// 私有化，账号密码注册
export const registerByPassword = ({ mobile, password, confirmPassword }: any) => {
  return request.post('/api/client/accounts/register-by-password', {
    mobile,
    password,
    confirmPassword
  });
};

// 获取图形验证码
export const getGraphicCaptchas = (sessionId: string) => {
  return request.get('/api/graphicCaptchas/code.jpg', {
    headers: {
      cookie: `session_csrf_id=${sessionId}`,
      responseType: 'arraybuffer'
    }
  });
};

/**
 * 验证图形验证码
 */
export const validateGraphicCaptchas = function ({ code, sessionId }: { code: string; sessionId: string }) {
  return authRequest.get(`/api/validateGraphicCaptchas?code=${code}&sessionId=${sessionId}`);
};

/**
 * 检验手机验证码
 */
export const validateCaptchas = function ({ mobile, type, code }: { mobile: string; type: number; code: string }) {
  return request.get(`/api/client/mobileCaptchas?mobile=${mobile}&type=${type}&code=${code}`);
};

/**
 * 获取认证配置（过渡方案）
 */
export const getAuthConfig = () => {
  return bffRequest.get('/bff/api/auth-config');
};

// 获取 RSA 公钥
export const getRSAPublicKey = () => {
  return request.get<DefaultSuccess<string>>('/api/pauth/token/public-key');
};

// RSA 密码登陆
export const loginByRSA = (params: { username: string; password: string; sessionId: string; graphicCaptcha?: string }) => {
  return request.post<DefaultSuccess<LoginSuccess>>(
    '/api/pauth/token/encrypted/password',
    {
      username: params.username,
      password: params.password,
      graphicCaptcha: params.graphicCaptcha
    },
    {
      headers: {
        'x-session-id': params.sessionId
      }
    }
  );
};

// RSA 密码注册
export const registerByRSA = (params: { username: string; password: string; confirmPassword: string }) => {
  return request.post<DefaultSuccess<LoginSuccess>>('/api/client/accounts/register-by-encrypted-password', {
    mobile: params.username,
    password: params.password,
    confirmPassword: params.confirmPassword
  });
};

// RSA 密码修改
export const modifyPasswordByRSA = (params: { newPassword: string; oldPassword: string; sessionId: string; graphicCaptcha?: string }) => {
  return request.patch<DefaultSuccess<LoginSuccess>>(
    '/api/user/account/encrypted-password-for-graphic-captcha',
    {
      password: params.newPassword,
      oldPassword: params.oldPassword,
      graphicCaptcha: params.graphicCaptcha
    },
    {
      headers: {
        'x-session-id': params.sessionId
      }
    }
  );
};
